<template>
    <div class="buyflow-panel">
        <div v-if="getServiceType === 'tivoServices'">
            <Service title="Premier TiVo Experience"
                     main-image="/assets/images/buyflow/smartbox-remote.png"
                     channel-strip="/assets/images/buyflow/logos-tivo-channels.png"
                     :price="getServicePrice(tivo)"
                     :is-selected="hasSelectedAService"
                     @select="selectTVOptions({ service: { decision: localsPlus.decision, selection: localsPlus }, equipment: { decision: tivo.decision, selection: tivo }, channels: { decision: tvOptions.channels.decision, selection: [] }, declined: false})">
                <p>Seamlessly integrates live TV, on-demand, recordings and streaming services into a single on-screen, interface while controlling it all with just your voice.
                <a v-if="channelCount"
                    href="#"
                   class="font-bold
                          underline"
                   @click.prevent="$emit('show-channel-lineup')">
                    Includes {{ channelCount }} channels
                </a>
                <span v-else>
                    Includes 40+ channels
                </span>
                    + 50 Music Choice channels.</p>
                <div class="py-4
                            flex
                            flex-col desktop:flex-row
                            justify-center desktop:justify-start
                            items-center
                            gap-2 desktop:gap-4">
                    <img alt="Channels include ABC, CBS, NBC, Fox, The CW, and more" class="desktop:w-54 desktop:h-6" src="/assets/images/buyflow/logos-tivo-channels.png" />
                    <span class="text-sm desktop:text-xs">+ more</span>
                </div>
            </Service>

            <div v-if="hasSelectedAService"
                 class="service-upgrades
                        desktop:grid-cols-2">
                <Upgrade upgrade-type="Service Upgrade"
                         title="Upgrade to Ultimate DVR"
                         icon="/assets/images/buyflow/icon-dvr.svg"
                         :price="tivoDVR.price"
                         cta-type="button"
                         :cta-button-is-selected="isServiceSelected(tivoDVR)"
                         :cta-button-allow-deselect="true"
                         @deselect="selectTVOptions({ equipment: { decision: tivo.decision, selection: tivo }})"
                         @select="selectTVOptions({ equipment: { decision: tivoDVR.decision, selection: tivoDVR }})">
                    Upgrade to TiVo DVR to get 300 hours of recording storage and never miss another show again.
                    <AppModal>
                        <template #trigger>
                            <a href="#"
                               class="font-bold
                                      underline">
                                See the benefits
                            </a>
                        </template>
                        <template #contents>
                            <div>
                                <table class="benefits">
                                    <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>TiVo Premier</th>
                                        <th>TiVo DVR Upgrade</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td>
                                            <div>TiVo Voice Remote</div>
                                            <div>TiVo Voice Remote lets you speak in your natural voice and gives you the ability to narrow your search results as you go.</div>
                                        </td>
                                        <td>
                                            <IconCheck class="inline mr-1" />
                                        </td>
                                        <td>
                                            <IconCheck class="inline mr-1" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>Instant Entertainment</div>
                                            <div>Say goodbye to juggling remotes or switching inputs and get all your entertainment in one place.</div>
                                        </td>
                                        <td>
                                            <IconCheck class="inline mr-1" />
                                        </td>
                                        <td>
                                            <IconCheck class="inline mr-1" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>Record It All</div>
                                            <div>With 6 tuners and 150 hours of HD recording capacity, you can say good-bye to recording conflicts! Record up to 6 shows at once and store up to 1,000 hours!</div>
                                        </td>
                                        <td></td>
                                        <td>
                                            <IconCheck class="inline mr-1" />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>Order PPV Entertainment</div>
                                            <div>Your home becomes the best seat in the house for exciting events like concerts, boxing, pro wrestling, and more.</div>
                                        </td>
                                        <td></td>
                                        <td>
                                            <IconCheck class="inline mr-1" />
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                    </AppModal>
                </Upgrade>

                <Upgrade v-if="isServiceSelected(tivo)"
                         upgrade-type="Additional Equipments"
                         title="Connect Additional TVs"
                         icon="/assets/images/buyflow/icon-addtv.svg"
                         :price="tivoSmartBox.price"
                         price-note="each"
                         cta-type="dropdown"
                         :cta-dropdown-value="extraEquipmentsCount"
                         :cta-dropdown-max="tivoSmartBox.max"
                         @cta-dropdown-changed="selectTVOptions({ extraTVBoxes: { decision: tivoSmartBox.decision, selection: tivoSmartBox, quantity: $event }})">
                    Make sure each TV is ready to go with an additional TiVo device.
                </Upgrade>

                <!-- wrapping this one in a div prevents Vue from "efficiently" reusing above's Upgrade component and incorrectly persisting the dropdown value -->
                <div v-if="isServiceSelected(tivoDVR)">
                    <Upgrade upgrade-type="Additional Equipments"
                             title="Connect Additional TVs"
                             icon="/assets/images/buyflow/icon-addtv.svg"
                             :price="tivoMini.price"
                             price-note="each"
                             cta-type="dropdown"
                             :cta-dropdown-value="extraEquipmentsCount"
                             :cta-dropdown-max="tivoMini.max"
                             @cta-dropdown-changed="selectTVOptions({ extraTVBoxes: { decision: tivoMini.decision, selection: tivoMini, quantity: $event.quantity }})">
                        Make sure each TV is ready to go with an additional TiVo device.
                    </Upgrade>
                </div>
            </div>
        </div>
        <div v-if="getServiceType === 'iptvServices'">
            <Service title="Stream TV"
                     :main-video="streamTVVideo"
                     :price="getServicePrice(streamTV)"
                     :is-selected="hasSelectedAService"
                     @select="selectTVOptions({ service: { decision: localsPlus.decision, selection: localsPlus }, equipment: { decision: streamTV.decision, selection: streamTV }, channels: { decision: tvOptions.channels.decision, selection: [] }, declined: false})">
                <p class="font-bold">Local broadcast channels are included with Breezeline Stream TV.</p>
                <p>Enjoy live TV, on-demand programming, and streaming services on a single unified interface. Watch from your TV, tablet or phone—and never miss your favorites again! Also, access thousands of Android TV apps in the Google Play Store.
                <a v-if="channelCount"
                   href="#"
                   class="font-bold
                          underline"
                   @click.prevent="$emit('show-channel-lineup')">
                    Includes {{ channelCount }} channels
                </a>
                <span v-else>
                    Includes 40+ channels
                </span>
                    + 50 Music Choice channels.</p>
                <div class="py-4
                            flex
                            flex-col desktop:flex-row
                            justify-center desktop:justify-start
                            items-center
                            gap-2 desktop:gap-4">
                    <img alt="Channels include ABC, CBS, NBC, Fox, The CW, and more" class="desktop:w-54 desktop:h-6" src="/assets/images/buyflow/logos-tivo-channels.png" />
                    <span class="text-sm desktop:text-xs">+ more</span>
                </div>
            </Service>
            <div v-if="hasSelectedAService"
                 class="service-upgrades
                        grid-cols-1 desktop:grid-cols-3">
                <div class="desktop:col-span-2
                            flex
                            flex-col desktop:flex-row">
                    <Upgrade upgrade-type="Service Upgrade"
                             title="Cloud DVR"
                             icon="/assets/images/buyflow/icon-dvr.svg"
                             :price="cloudDVR.price"
                             cta-type="button"
                             :cta-button-is-selected="isServiceSelected(cloudDVR)"
                             :cta-button-allow-deselect="true"
                             :cta-button-mini="true"
                             @deselect="selectTVOptions({ equipment: { decision: streamTV.decision, selection: streamTV }})"
                             @select="selectTVOptions({ equipment: { decision: cloudDVR.decision, selection: [streamTV, cloudDVR] }})"
                             class="desktop:w-1/2
                                    pb-6 desktop:pb-0
                                    desktop:pr-6">
                        Ready for the next level? Cloud DVR lets you record 150 hours of movies and shows at the same time. Watch them from any of your devices, anytime, anywhere.
                        <AppModal>
                            <template #trigger>
                                <IconCircleInfo class="inline cursor-pointer" />
                            </template>
                            <template #contents>
                                <div class="text-sm
                                        font-normal
                                        break-words">
                                    Some video programming content is unavailable for viewing outside of your service address location.
                                </div>
                            </template>
                        </AppModal>
                    </Upgrade>
                    <Upgrade upgrade-type="Service Upgrade"
                             title="Cloud DVR Max"
                             icon="/assets/images/buyflow/icon-dvr.svg"
                             :price="cloudDVRMax.price"
                             divider-text="or"
                             cta-type="button"
                             :cta-button-is-selected="isServiceSelected(cloudDVRMax)"
                             :cta-button-allow-deselect="true"
                             :force-right-align-text="false"
                             :cta-button-mini="true"
                             @deselect="selectTVOptions({ equipment: { decision: streamTV.decision, selection: streamTV }})"
                             @select="selectTVOptions({ equipment: { decision: cloudDVRMax.decision, selection: [streamTV, cloudDVRMax] }})"
                             class="desktop:w-1/2
                                    pt-10 desktop:pt-0
                                    desktop:pl-6
                                    border-t desktop:border-t-0
                                    desktop:border-l
                                    border-gray-200
                                    border-dashed">
                        Need even more? With 300 hours you get twice the storage space for your favorite programming!
                        <AppModal>
                            <template #trigger>
                                <IconCircleInfo class="inline cursor-pointer" />
                            </template>
                            <template #contents>
                                <div class="text-sm
                                        font-normal
                                        break-words">
                                    Some video programming content is unavailable for viewing outside of your service address location.
                                </div>
                            </template>
                        </AppModal>
                    </Upgrade>
                </div>
                <Upgrade upgrade-type="Additional Equipments"
                         title="Additional Stream TV Box"
                         icon="/assets/images/buyflow/icon-addtv.svg"
                         :price="extraStreamTVBoxes.price"
                         price-note="each"
                         cta-type="dropdown"
                         :cta-dropdown-value="extraEquipmentsCount"
                         :cta-dropdown-max="extraStreamTVBoxes.max"
                         @cta-dropdown-changed="selectTVOptions({ extraTVBoxes: { decision: extraStreamTVBoxes.decision, selection: extraStreamTVBoxes, quantity: $event }})">
                    Make sure each TV is ready to go with an additional Stream TV Box.
                </Upgrade>
            </div>
        </div>
    </div>
</template>

<script>
    import AppModal from "~/Buyflow/components/AppModal"
    import Service from "./service"
    import Upgrade from "./upgrade"
    import IconCheck from "@/atoms/icons/IconCheck"
    import IconCircleInfo from "@/atoms/icons/IconCircleInfo"
    import IconPlus from "@/atoms/icons/IconPlus"

    export default {
        name: 'TVServices',
        components: {
            Service,
            Upgrade,
            AppModal,
            IconCheck,
            IconCircleInfo,
            IconPlus,
        },
        props: {
            tvData: {
                type: Object,
                required: true,
            },
            tvOptions: {
                type: Object,
                required: true,
            },
            installData: {
                type: Object,
                required: true,
            },
            getServiceType: {
                type: String,
                required: true,
                validator: val => [
                    'tivoServices',
                    'iptvServices',
                ].includes(val),
            },
            getServicePrice: {
                type: Function,
                default: () => 0,
            },
            isServiceSelected: {
                type: Function,
                default: () => false,
            },
            hasSelectedAService: {
                type: Boolean,
                default: false,
            },
            hasSelectedSelfInstall: {
                type: Boolean,
                default: false,
            },
            extraEquipmentsCount: {
                type: Number,
                default: 0,
            },
            channelCount: {
                type: Number,
                default: 0,
            },
            marketType: {
                type: String,
                required: true
            }
        },
        computed: {
            localsPlus () {
                return this.tvData.packages.localsPlus || null
            },
            tivo () {
                return this.tvData.tivoServices.tivo || null
            },
            tivoDVR () {
                return this.tvData.tivoServices.tivoDVR || null
            },
            tivoSmartBox () {
                return this.tvData.tivoEquipments.tivoSmartBox || null
            },
            tivoMini () {
                return this.tvData.tivoEquipments.tivoMini || null
            },
            streamTV () {
                return this.tvData.iptvServices.streamTV || null
            },
            cloudDVR () {
                return this.tvData.iptvServices.cloudDVR || null
            },
            cloudDVRMax () {
                return this.tvData.iptvServices.cloudDVRMax || null
            },
            extraStreamTVBoxes () {
                return this.tvData.iptvEquipments.extraStreamTVBoxes || null
            },
            streamTVVideo () {
                return {
                    videoId: 'xe3nLAdKIoY',
                    thumbnail: '/temp/video-player/stream-tv.jpg',
                    thumbnailAlt: '',
                    caption: 'What is StreamTV?',
                }
            },
        },
        methods: {
            selectTVOptions (options) {
                this.$emit('select-service', options)
            },
        },
    }
</script>

<style lang="scss" scoped>
    .service-upgrades {
        @apply block
               mt-6
               border-t
               border-gray-200
               pt-6
               grid
               gap-6;

        > :last-child {
            @apply border-t desktop:border-t-0
                   desktop:border-l
                   desktop:border-gray-200
                   pt-6 desktop:pt-0
                   desktop:pl-6;
        }
    }

    table.benefits {
        @apply border
               border-gray-200
               rounded;

        thead {
            @apply text-center
                   bg-gray-100;
        }

        th, td {
            @apply p-2 desktop:p-4
                   border
                   border-gray-200
                   text-center;
        }

        td:first-child {
            @apply text-left;
        }

        td:nth-child(n+2) {
            @apply text-teal
                   text-base desktop:text-xl;
        }

        th {
            @apply text-sm desktop:text-base
                   font-bold;
        }

        td {
            @apply text-xs desktop:text-sm;
        }

        td div:first-child {
            @apply text-sm desktop:text-base
                   font-bold;
        }
    }
</style>
